import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiClient } from "../lib/api";
import { useAuth } from "./AuthContext";

const MasteryCtx = createContext(null);

const MILESTONES = [5, 10, 25, 50, 75, 100, 150, 200, 300, 500, 750, 1000];

function milestoneMessage(count) {
  const map = {
    5:   "🎉 Five mastered — you're on a roll!",
    10:  "🏅 TEN mastered! That's recital-worthy!",
    25:  "🌟 25 mastered — you're a rising star!",
    50:  "🚀 FIFTY mastered! Half a century of victories!",
    75:  "💎 75 mastered — diamond-level practice!",
    100: "👑 100 MASTERED! You're piano royalty now!",
    150: "🔥 150! Absolutely on fire!",
    200: "⚡ 200! Virtuoso status unlocked!",
    300: "🎼 300! A living legend in the making!",
    500: "🏆 FIVE HUNDRED! Hall-of-Fame territory!",
    750: "🌠 750! Few ever get here. Bravo!",
    1000:"🎹 ONE THOUSAND!! You ARE the piano.",
  };
  return map[count] || null;
}

export function MasteryProvider({ children }) {
  const { user } = useAuth();
  const [mastery, setMastery] = useState({ scales: 0, songs: 0, total: 0, scales_total: 12 });
  const [celebration, setCelebration] = useState(null); // { kind, title, subtitle, milestone }
  const hasLoaded = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await apiClient.get("/mastery/me");
      setMastery(r.data);
      hasLoaded.current = true;
    } catch (e) { console.warn("Mastery fetch failed", e); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const celebrate = useCallback(({ kind, itemName }) => {
    const titles = {
      scale: `🎹 ${itemName} major — MASTERED!`,
      song:  `🎵 ${itemName} — MASTERED!`,
    };
    const regular = [
      "You absolutely crushed it.",
      "Superstar status unlocked!",
      "Your piano is so proud.",
      "Another one for the trophy case.",
      "Keep going — more to conquer!",
    ];
    // The newly-mastered item becomes total+1
    const expectedTotal = (mastery.total || 0) + 1;
    const isMilestone = MILESTONES.includes(expectedTotal);
    const milestoneMsg = isMilestone ? milestoneMessage(expectedTotal) : null;

    setCelebration({
      kind,
      title: titles[kind] || "MASTERED!",
      subtitle: milestoneMsg || regular[Math.floor(Math.random() * regular.length)],
      milestone: isMilestone ? expectedTotal : null,
    });
    refresh();
  }, [mastery.total, refresh]);

  const dismiss = useCallback(() => setCelebration(null), []);

  const value = useMemo(
    () => ({ mastery, celebrate, dismiss, celebration, refresh }),
    [mastery, celebrate, dismiss, celebration, refresh],
  );

  return (
    <MasteryCtx.Provider value={value}>
      {children}
    </MasteryCtx.Provider>
  );
}

export const useMastery = () => useContext(MasteryCtx);
